import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createCookieConsentCookie, storeCookieConsent } from '../../user/consents/consent-actions';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import { hasDocument } from '../../utils';
import { Link, matchPath, useLocation } from 'react-router';
import styles from './modal-cookie-consent.module.less';
import { useFeature, useFeatureExperimentVariant } from '../hooks/isFeatureEnabled';
import { CONSENT_MODAL_ENABLED, PROJECT_SHOW_ADSENSE } from '../../constants/features/constants';
import { getCookieConsent } from '../../user/consents/consent-reducer';
import { BodyText, Heading2 } from '@sb1/ffe-core-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '@sb1/ffe-buttons-react';
import Modal from '../modal/Modal';
import AdministerCookieContent from './AdministerCookiesContent';

const BLACKLISTED_PATHS = [
    '/project/:projectId/embed',
    '/project/embed',
    '/cause/:causeId/embed',
    '/project/selection/:id/banner',
    '/project/:projectId/payment',
    '/project/:projectId/payment/vipps/:vippsId',
    '/payment/vipps/:vippsId',
    '/organization/register/verified',
    '/verification/profile',
    '/vipps/login/success',
    '/google/login/success',
    '/facebook/login/success',
    '/personvern',
    '/project/:projectId/overlay',
    '/project/:projectId/overlay-transactions',
    '/project/:projectId/overlay-collected',
    '/project/:projectId/overlay-polls/:pollId',
];
const TRACKING_CATEGORY = 'cookie-consent';
const TRACKING_NAME = 'popup';

const ModalCookieConsent = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [statisticsInfo, setStatisticsInfo] = useState(false);
    const [retargetingInfo, setRetargetingInfo] = useState(false);
    const cookieConsent = useSelector((state) => getCookieConsent(state));
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const defaultStatisticsCookieState = cookieConsent ? cookieConsent.statistics : false;
    const defaultRetargetingCookieState = cookieConsent ? cookieConsent.retargeting : false;
    const consentModalFeatureEnabled = useFeature(CONSENT_MODAL_ENABLED);
    const googleConsentPopupFeatureEnabled = useFeatureExperimentVariant(PROJECT_SHOW_ADSENSE);
    const shouldShowGoogleConsentPopup =
        googleConsentPopupFeatureEnabled && matchPath('/project/:projectId', location.pathname);
    const consentModalEnabled = consentModalFeatureEnabled && !shouldShowGoogleConsentPopup;

    const [statisticsCookiesConsent, setStatisticsCookiesConsent] = useState(defaultStatisticsCookieState);
    const [retargetingCookiesConsent, setRetargetingCookiesConsent] = useState(defaultRetargetingCookieState);

    const documentIsDefined = hasDocument();

    const closeModal = () => {
        if (showConsentModal && hasMadeChanges()) {
            window.location.reload();
        }
        setShowConsentModal(false);
        trackEvent(TRACKING_CATEGORY, TRACKING_NAME, { action: 'close' });
    };

    const hasMadeChanges = () =>
        statisticsCookiesConsent !== defaultStatisticsCookieState ||
        retargetingCookiesConsent !== defaultRetargetingCookieState;

    const consentToAll = () => {
        dispatch(
            storeCookieConsent({
                cookies: {
                    ...cookieConsent,
                    statistics: true,
                    retargeting: true,
                },
            })
        );
        trackEvent(TRACKING_CATEGORY, TRACKING_NAME, { action: 'consent-all' });
        closeModal(true);
    };

    const consentToSelected = () => {
        dispatch(
            storeCookieConsent({
                cookies: {
                    ...cookieConsent,
                    statistics: statisticsCookiesConsent,
                    retargeting: retargetingCookiesConsent,
                },
            })
        );
        trackEvent(TRACKING_CATEGORY, TRACKING_NAME, {
            action: 'consent-selected',
            statistics: statisticsCookiesConsent,
            retargeting: retargetingCookiesConsent,
        });
        closeModal();
    };

    const rejectAll = () => {
        createCookieConsentCookie({ cookieObject: { bannerClosed: true } });
        trackEvent(TRACKING_CATEGORY, TRACKING_NAME, {
            action: 'reject-all',
        });
        closeModal();
    };

    useEffect(() => {
        trackEvent(TRACKING_CATEGORY, TRACKING_NAME, { action: 'load' });
    }, []);

    useEffect(() => {
        if (documentIsDefined) {
            const isBlacklisted = !!BLACKLISTED_PATHS.find((path) => matchPath(path, location.pathname));
            if (!isBlacklisted && !cookieConsent && consentModalEnabled) {
                setShowConsentModal(true);
                trackEvent(TRACKING_CATEGORY, TRACKING_NAME, { action: 'show' });
            }
        }
    }, [documentIsDefined, cookieConsent, consentModalEnabled]);

    return (
        <Modal
            isOpen={showConsentModal}
            className={'ffe-modal'}
            containerClassName={['ffe-modal__body', styles.modalContainer].join(' ')}
            onRequestClose={() => {
                closeModal();
            }}
            shouldCloseOnOverlayClick={false}
        >
            <div className={[styles.contentContainer, 'ffe-modal__block'].join(' ')}>
                {showOptions ? (
                    <>
                        <AdministerCookieContent
                            setStatisticsCookiesConsent={setStatisticsCookiesConsent}
                            statisticsCookiesConsent={statisticsCookiesConsent}
                            setRetargetingCookiesConsent={setRetargetingCookiesConsent}
                            retargetingCookiesConsent={retargetingCookiesConsent}
                            statisticsInfo={statisticsInfo}
                            setStatisticsInfo={setStatisticsInfo}
                            retargetingInfo={retargetingInfo}
                            setRetargetingInfo={setRetargetingInfo}
                        />
                        <Link
                            to={'/personvern'}
                            onClick={() => {
                                setShowConsentModal(false);
                                trackEvent(TRACKING_CATEGORY, TRACKING_NAME, { action: 'show-more-info' });
                            }}
                        >
                            Les mer om informasjonskapsler og personvern på Spleis
                        </Link>
                        <div className={styles.buttonsContainer}>
                            <SecondaryButton
                                onClick={() => {
                                    setRetargetingInfo(false);
                                    setStatisticsInfo(false);
                                    setShowOptions(false);
                                    trackEvent(TRACKING_CATEGORY, TRACKING_NAME, { action: 'go-back' });
                                }}
                                size="lg"
                            >
                                Avbryt
                            </SecondaryButton>
                            <PrimaryButton size="lg" onClick={consentToSelected}>
                                Godta valgte
                            </PrimaryButton>
                        </div>
                    </>
                ) : (
                    <>
                        <Heading2 id={'consent-modal-title'} noMargin>
                            Bruk av cookies
                        </Heading2>
                        <BodyText>
                            Vi bruker cookies for at Spleis skal fungere best mulig, til å samle statistikk, og tilby
                            tilpasset markedsføring.
                        </BodyText>

                        <div className={styles.buttonsContainer}>
                            <TertiaryButton
                                className={styles.customCookies}
                                size="lg"
                                onClick={() => {
                                    setShowOptions(true);
                                    trackEvent(TRACKING_CATEGORY, TRACKING_NAME, { action: 'show-selection' });
                                }}
                            >
                                Tilpass cookies
                            </TertiaryButton>
                            <PrimaryButton size="lg" onClick={rejectAll}>
                                Avvis
                            </PrimaryButton>
                            <PrimaryButton size="lg" onClick={consentToAll}>
                                Godta
                            </PrimaryButton>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ModalCookieConsent;
